import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Calendar, PlusCircle, User, Link as LinkIcon, Trash2, Loader2, Pencil,
    FileText, Pill, TrendingUp, ClipboardList, LayoutDashboard, StickyNote,
    Download, AlertTriangle, ChevronRight, Stethoscope, FlaskConical, Activity, History, Calculator,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import CaregiverManager from "@/components/CaregiverManager";
import ExamsTimeline from "@/components/ExamsTimeline";
import ClinicalTimeline from "@/components/ClinicalTimeline";
import ClinicalCalculators from "@/components/ClinicalCalculators";
import VitalsFlowsheet from "@/components/VitalsFlowsheet";
import ProblemList from "@/components/ProblemList";
import SnapshotHub from "@/components/SnapshotHub";
import PatientHeaderBar from "@/components/PatientHeaderBar";
import ExamUploadDialog from "@/components/ExamUploadDialog";
import FormattedMarkdown from "@/components/FormattedMarkdown";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { CONSULTATION_TYPES, STATUS_LABELS, computeNpiTotals } from "@/lib/aga";

const typeLabel = Object.fromEntries(CONSULTATION_TYPES.map((t) => [t.value, t.label]));

const TABS = [
    { key: "resumo", label: "Resumo", icon: LayoutDashboard },
    { key: "linha_tempo", label: "Linha do Tempo", icon: History },
    { key: "problemas", label: "Problemas", icon: ClipboardList },
    { key: "calculadoras", label: "Calculadoras", icon: Calculator },
    { key: "consultas", label: "Consultas", icon: Calendar },
    { key: "exames", label: "Exames", icon: FlaskConical },
    { key: "sinais_vitais", label: "Sinais Vitais", icon: Activity },
    { key: "aga_temporal", label: "Evolução AGA", icon: TrendingUp },
    { key: "evolucoes", label: "Evoluções", icon: StickyNote },
    { key: "planos", label: "Planos de Cuidado", icon: FileText },
    { key: "medicacoes", label: "Medicações", icon: Pill },
    { key: "documentos", label: "Documentos", icon: User },
];

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return iso; }
}
function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

export default function PatientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [chart, setChart] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [tab, setTab] = useState("resumo");
    const [deleting, setDeleting] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [loading, setLoading] = useState(true);
    const [showAttachExam, setShowAttachExam] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [chartRes, snapRes] = await Promise.all([
                    api.get(`/patients/${id}/chart`),
                    api.get(`/patients/${id}/snapshot`).catch(() => ({ data: null })),
                ]);
                setChart(chartRes.data);
                setSnapshot(snapRes.data);
            } catch (e) {
                toast.error(formatApiError(e));
            } finally { setLoading(false); }
        })();
    }, [id]);

    const patient = chart?.patient;
    const canDelete = user && (user.role === "doctor" || user.role === "admin");
    const canCreateConsult = user && (user.role === "doctor" || user.role === "admin");

    const startNewConsultation = () => {
        // Pass patient id + a marker so NewConsultation can show the continuity banner
        navigate(`/consultations/new?patient=${id}&from=chart`);
    };

    const handleDelete = async () => {
        if (confirmText.trim() !== patient.full_name.trim()) {
            toast.error("Digite o nome exato do paciente para confirmar.");
            return;
        }
        setDeleting(true);
        try {
            const { data } = await api.delete(`/patients/${id}`);
            const d = data.deleted || {};
            toast.success(
                `Paciente excluído. Registros removidos: ${d.consultations || 0} consultas, ${d.care_plans || 0} planos, ${d.evolutions || 0} evoluções.`,
                { duration: 6000 }
            );
            navigate("/dashboard");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setDeleting(false); }
    };

    if (loading || !chart || !patient) return <AppShell><p>Carregando prontuário…</p></AppShell>;

    return (
        <AppShell>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <button onClick={() => navigate(-1)} className="btn-ghost" data-testid="chart-back-btn">
                    <ArrowLeft size={16} /> Voltar
                </button>
                <div className="flex items-center gap-2">
                    {canDelete && (
                        <Link
                            to={`/patients/${id}/edit`}
                            data-testid="edit-patient-btn"
                            className="text-sm px-3 py-1.5 rounded-lg text-brand border border-brand hover:bg-alt transition-colors flex items-center gap-1.5"
                        >
                            <Pencil size={14} /> Editar dados
                        </Link>
                    )}
                    {canDelete && (
                        <button
                            data-testid="delete-patient-btn"
                            onClick={() => { setShowConfirm(true); setConfirmText(""); }}
                            className="text-sm px-3 py-1.5 rounded-lg text-[var(--status-danger)] border border-[var(--status-danger)] hover:bg-[var(--status-danger-bg)] transition-colors flex items-center gap-1.5"
                        >
                            <Trash2 size={14} /> Excluir paciente
                        </button>
                    )}
                </div>
            </div>

            {showConfirm && (
                <DeleteConfirm
                    patient={patient}
                    confirmText={confirmText}
                    setConfirmText={setConfirmText}
                    deleting={deleting}
                    onCancel={() => setShowConfirm(false)}
                    onConfirm={handleDelete}
                />
            )}

            {/* Sticky patient header — Epic-style */}
            <PatientHeaderBar
                snapshot={snapshot}
                isProfessional={canCreateConsult}
                onNewConsult={canCreateConsult ? startNewConsultation : null}
                onAttachExam={canCreateConsult ? () => setShowAttachExam(true) : null}
            />

            {showAttachExam && (
                <ExamUploadDialog
                    patientId={id}
                    onClose={() => setShowAttachExam(false)}
                    onSaved={() => setShowAttachExam(false)}
                />
            )}

            {/* Legacy consultation counters — kept in the tab strip area */}
            <div className="card-earthy mt-4 mb-4">
                <div className="flex flex-wrap gap-3 text-xs">
                    <Badge label="Consultas" value={chart.consultations.length} testId="chart-badge-consultas" />
                    <Badge label="Planos de cuidado" value={chart.care_plans.length} testId="chart-badge-planos" />
                    <Badge label="Evoluções" value={chart.evolutions.length} testId="chart-badge-evolucoes" />
                    <Badge label="Exames" value={(chart.exams || []).length} testId="chart-badge-exames" />
                </div>
            </div>

            {/* Tab strip */}
            <div className="mb-4 flex flex-wrap gap-1 border-b border-[var(--border-organic)]">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`chart-tab-${t.key}`}
                            className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                                active
                                    ? "border-brand text-brand font-medium"
                                    : "border-transparent text-muted-clinical hover:text-brand"
                            }`}
                        >
                            <Icon size={14} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "resumo" && <SnapshotHub patientId={id} onOpenTab={setTab} />}
            {tab === "linha_tempo" && <ClinicalTimeline patientId={id} />}
            {tab === "problemas" && (
                <ProblemList
                    patientId={id}
                    canEdit={user && (user.role === "doctor" || user.role === "admin")}
                />
            )}
            {tab === "calculadoras" && <ClinicalCalculators patientId={id} />}
            {tab === "consultas" && <ConsultasTab chart={chart} />}
            {tab === "exames" && (
                <ExamsTimeline
                    patientId={id}
                    canUpload={true}
                    canEdit={user && (user.role === "doctor" || user.role === "admin" || user.role === "patient" || user.role === "caregiver")}
                />
            )}
            {tab === "sinais_vitais" && <VitalsFlowsheet patientId={id} />}
            {tab === "aga_temporal" && <AGATemporalTab chart={chart} />}
            {tab === "evolucoes" && <EvolucoesTab chart={chart} />}
            {tab === "planos" && <PlanosTab chart={chart} />}
            {tab === "medicacoes" && <MedicacoesTab chart={chart} />}
            {tab === "documentos" && <DocumentosTab chart={chart} patientId={id} />}
        </AppShell>
    );
}

function Badge({ label, value, testId }) {
    return (
        <div className="px-3 py-1.5 rounded-lg bg-alt text-brand-secondary" data-testid={testId}>
            <span className="font-semibold text-brand">{value}</span> <span className="text-muted-clinical">{label}</span>
        </div>
    );
}

// ---------- Tab: Resumo ----------
function ResumoTab({ chart, onNewConsult }) {
    const { patient, latest_assessment, latest_care_plan, consultations, care_plans, assessments, doctors, evolutions } = chart;
    const lastConsult = consultations[0];
    const lastDoctor = lastConsult ? doctors[lastConsult.doctor_id] : null;

    // As consultas agora são registros individuais. Para o Resumo, buscamos o
    // último plano de cuidado que realmente possui problemas cadastrados e a
    // última AGA com dados clínicos — para não zerar a visão do prontuário
    // logo após criar uma consulta de retorno em branco.
    const referenceCarePlan = (care_plans || []).find((cp) => (cp.problems || []).length > 0) || latest_care_plan;
    const referenceAssessment = (assessments || [])
        .slice()
        .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""))
        .find((a) => {
            const d = a?.data || {};
            const keys = Object.keys(d);
            return keys.length > 0 && !(keys.length === 1 && keys[0] === "medicamentos");
        }) || latest_assessment;

    // Compute active problems from best-available care plan
    const activeProblems = (referenceCarePlan?.problems || []).filter((p) => {
        const goals = p?.goals || [];
        if (goals.length === 0) return true;
        return goals.some((g) => !g.achieved);
    });

    // Medications: pull from latest assessment (o carry-forward mantém as ativas)
    const meds = extractMeds(latest_assessment?.data);

    // NPI-Q & falls quick alerts — usam a AGA mais recente com dados clínicos
    const npiTotals = referenceAssessment?.data?.npi_q ? computeNpiTotals(referenceAssessment.data.npi_q) : null;
    const falls = referenceAssessment?.data?.quedas || referenceAssessment?.data?.avaliacao_quedas;

    return (
        <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
                <div className="card-earthy" data-testid="resumo-last-consult">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <h3 className="font-display text-lg font-medium text-brand flex items-center gap-2">
                            <Stethoscope size={18} /> Última consulta
                        </h3>
                        {lastConsult && (
                            <Link to={`/consultations/${lastConsult.id}`} className="text-xs text-brand hover:underline flex items-center gap-1" data-testid="resumo-open-last-consult">
                                Abrir consulta <ChevronRight size={12} />
                            </Link>
                        )}
                    </div>
                    {lastConsult ? (
                        <div className="text-sm space-y-1">
                            <p><span className="text-muted-clinical">Data:</span> <strong>{fmtDateTime(lastConsult.scheduled_at || lastConsult.created_at)}</strong></p>
                            <p><span className="text-muted-clinical">Modalidade:</span> {typeLabel[lastConsult.consultation_type]}</p>
                            <p><span className="text-muted-clinical">Status:</span> {STATUS_LABELS[lastConsult.status]}</p>
                            {lastDoctor && <p><span className="text-muted-clinical">Profissional:</span> {lastDoctor.name}</p>}
                        </div>
                    ) : (
                        <div className="text-sm">
                            <p className="text-muted-clinical mb-3">Nenhuma consulta registrada. Comece o prontuário deste paciente:</p>
                            <button onClick={onNewConsult} className="btn-primary text-sm"><PlusCircle size={14} /> Criar primeira consulta</button>
                        </div>
                    )}
                </div>

                <div className="card-earthy" data-testid="resumo-active-problems">
                    <h3 className="font-display text-lg font-medium text-brand mb-3 flex items-center gap-2">
                        <ClipboardList size={18} /> Problemas ativos
                    </h3>
                    {activeProblems.length === 0 ? (
                        <p className="text-sm text-muted-clinical">Nenhum problema ativo no último plano de cuidado.</p>
                    ) : (
                        <ul className="space-y-2">
                            {activeProblems.map((p, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                    <PriorityDot priority={p.priority} />
                                    <div className="flex-1">
                                        <p className="font-medium text-brand">{p.title || "Problema"}</p>
                                        {p.description && <p className="text-xs text-muted-clinical mt-0.5">{p.description}</p>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="card-earthy" data-testid="resumo-meds">
                    <h3 className="font-display text-lg font-medium text-brand mb-3 flex items-center gap-2">
                        <Pill size={18} /> Medicações em uso (última consulta)
                    </h3>
                    <MedsList meds={meds} />
                </div>

                <div className="card-earthy" data-testid="resumo-recent-evolutions">
                    <h3 className="font-display text-lg font-medium text-brand mb-3 flex items-center gap-2">
                        <StickyNote size={18} /> Últimas evoluções
                    </h3>
                    {evolutions.length === 0 ? (
                        <p className="text-sm text-muted-clinical">Sem evoluções registradas.</p>
                    ) : (
                        <ul className="space-y-3">
                            {evolutions.slice(0, 3).map((ev) => (
                                <li key={ev.id} className="border-l-2 border-brand/30 pl-3">
                                    <p className="text-xs text-muted-clinical mb-1">{fmtDateTime(ev.created_at)} · {typeLabel[ev.consultation_type] || "—"}</p>
                                    <p className="text-sm text-brand-secondary line-clamp-3 whitespace-pre-wrap">{ev.content || ev.notes || "—"}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <div className="space-y-4">
                <div className="card-earthy" data-testid="resumo-alerts">
                    <h3 className="font-display text-lg font-medium text-brand mb-3 flex items-center gap-2">
                        <AlertTriangle size={18} className="text-[var(--status-danger)]" /> Sinais de alerta
                    </h3>
                    <AlertsList meds={meds} npiTotals={npiTotals} falls={falls} />
                </div>

                <div className="card-earthy" data-testid="resumo-identity">
                    <h3 className="font-display text-sm font-medium text-brand mb-2">Identificação</h3>
                    <dl className="text-sm space-y-1.5">
                        <Row label="CPF" val={patient.cpf} />
                        <Row label="Telefone" val={patient.phone} />
                        <Row label="Endereço" val={patient.address} />
                        <Row label="Cidade" val={patient.city_uf} />
                    </dl>
                    <div className="divider-organic" />
                    <h3 className="font-display text-sm font-medium text-brand mb-2">Cuidador</h3>
                    <dl className="text-sm space-y-1.5">
                        <Row label="Nome" val={patient.caregiver_name} />
                        <Row label="Parentesco" val={patient.caregiver_relationship} />
                        <Row label="Telefone" val={patient.caregiver_phone} />
                    </dl>
                </div>
            </div>
        </div>
    );
}

function PriorityDot({ priority }) {
    const color = priority === "alta" ? "bg-[var(--status-danger)]" : priority === "baixa" ? "bg-[var(--status-success)]" : "bg-[var(--status-warning)]";
    return <span className={`inline-block w-2 h-2 rounded-full mt-1.5 ${color}`} title={priority || "média"} />;
}

function MedsList({ meds }) {
    if (!meds || meds.length === 0) {
        return <p className="text-sm text-muted-clinical">Nenhum medicamento registrado.</p>;
    }
    return (
        <ul className="space-y-1.5 text-sm">
            {meds.slice(0, 15).map((m, i) => (
                <li key={i} className="flex items-baseline gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand" />
                    <span>
                        <strong className="text-brand">{m.name || m.medicamento || m.drug || "Medicação"}</strong>
                        {m.dose && <span className="text-muted-clinical"> · {m.dose}</span>}
                        {m.frequency && <span className="text-muted-clinical"> · {m.frequency}</span>}
                    </span>
                </li>
            ))}
            {meds.length > 15 && <li className="text-xs text-muted-clinical">+{meds.length - 15} outras</li>}
        </ul>
    );
}

function AlertsList({ meds, npiTotals, falls }) {
    const alerts = [];
    if (meds && meds.length >= 5) {
        alerts.push({ level: "warning", text: `Polifarmácia: ${meds.length} medicações em uso.` });
    }
    if (npiTotals) {
        if (npiTotals.gravidade >= 12) alerts.push({ level: "danger", text: `NPI-Q severidade elevada (${npiTotals.gravidade}).` });
        if (npiTotals.estresse >= 20) alerts.push({ level: "danger", text: `Sobrecarga do cuidador (NPI-Q estresse ${npiTotals.estresse}).` });
    }
    if (falls) {
        const fallsText = typeof falls === "string" ? falls : (falls.numero_quedas_12m || falls.historico || "");
        const n = parseInt(fallsText, 10);
        if (!Number.isNaN(n) && n >= 1) {
            alerts.push({ level: "warning", text: `Histórico de quedas: ${n} nos últimos 12 meses.` });
        }
    }
    if (alerts.length === 0) {
        return <p className="text-sm text-muted-clinical">Nenhum sinal de alerta detectado automaticamente.</p>;
    }
    return (
        <ul className="space-y-2 text-sm">
            {alerts.map((a, i) => (
                <li key={i} className={`p-2 rounded border-l-4 ${a.level === "danger" ? "border-[var(--status-danger)] bg-[var(--status-danger-bg)]" : "border-[var(--status-warning)] bg-[var(--status-warning-bg)]"}`}>
                    {a.text}
                </li>
            ))}
        </ul>
    );
}

function Row({ label, val }) {
    return (
        <div className="flex justify-between gap-2">
            <dt className="text-muted-clinical">{label}</dt>
            <dd className="text-right font-medium">{val || "—"}</dd>
        </div>
    );
}

// ---------- Tab: Consultas ----------
function ConsultasTab({ chart }) {
    const { consultations, doctors } = chart;
    if (consultations.length === 0) {
        return <div className="card-earthy text-sm text-muted-clinical">Nenhuma consulta registrada.</div>;
    }
    return (
        <div className="space-y-2" data-testid="consultas-tab-list">
            {consultations.map((c) => {
                const doc = doctors[c.doctor_id];
                return (
                    <Link key={c.id} to={`/consultations/${c.id}`} className="card-earthy block hover:shadow-md transition-shadow" data-testid={`consulta-card-${c.id}`}>
                        <div className="flex items-start justify-between flex-wrap gap-2">
                            <div>
                                <div className="text-sm font-medium text-brand flex items-center gap-2">
                                    <Calendar size={14} /> {typeLabel[c.consultation_type]}
                                </div>
                                <div className="text-xs text-muted-clinical mt-1">
                                    {fmtDateTime(c.scheduled_at || c.created_at)}
                                    {doc && ` · ${doc.name}`}
                                    {c.carried_from_at && ` · referência: ${fmtDate(c.carried_from_at)}`}
                                </div>
                                {c.notes && <p className="text-xs text-brand-secondary mt-2 line-clamp-2">{c.notes}</p>}
                            </div>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-alt text-brand-secondary">{STATUS_LABELS[c.status]}</span>
                        </div>
                    </Link>
                );
            })}
        </div>
    );
}

// ---------- Tab: AGA temporal ----------
const AGA_METRICS = [
    { key: "mmse", label: "Mini-Mental (MMSE)", from: (d) => pickScore(d?.avaliacao_cognitiva, ["mmse", "mmse_score", "minimental"]) },
    { key: "moca", label: "MoCA", from: (d) => pickScore(d?.avaliacao_cognitiva, ["moca", "moca_score"]) },
    { key: "katz", label: "Katz (ABVD)", from: (d) => pickScore(d?.avaliacao_funcional, ["katz", "katz_score", "abvd"]) },
    { key: "lawton", label: "Lawton (AIVD)", from: (d) => pickScore(d?.avaliacao_funcional, ["lawton", "lawton_score", "aivd"]) },
    { key: "gds", label: "GDS-15", from: (d) => pickScore(d?.avaliacao_humor, ["gds", "gds_15", "gds_score"]) },
    { key: "npi_q_grav", label: "NPI-Q Gravidade", from: (d) => d?.npi_q ? computeNpiTotals(d.npi_q).gravidade : null },
    { key: "npi_q_estr", label: "NPI-Q Estresse (Zarit)", from: (d) => d?.npi_q ? computeNpiTotals(d.npi_q).estresse : null },
    { key: "quedas", label: "Quedas (12m)", from: (d) => pickScore(d?.quedas || d?.avaliacao_quedas, ["numero_quedas_12m", "quedas_12m", "numero"]) },
];

function pickScore(obj, keys) {
    if (!obj || typeof obj !== "object") return null;
    for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") {
            const n = Number(obj[k]);
            if (!Number.isNaN(n)) return n;
        }
    }
    return null;
}

function AGATemporalTab({ chart }) {
    const { assessments, consultations } = chart;
    // Order assessments ASC (older -> newer) matching consultations
    const byConsult = new Map();
    for (const a of assessments) byConsult.set(a.consultation_id, a);
    const timeline = [...consultations]
        .slice()
        .sort((a, b) => (a.scheduled_at || a.created_at || "").localeCompare(b.scheduled_at || b.created_at || ""))
        .map((c) => ({ consult: c, assessment: byConsult.get(c.id) }));

    if (timeline.length === 0) {
        return <div className="card-earthy text-sm text-muted-clinical">Sem consultas para comparar.</div>;
    }

    return (
        <div className="card-earthy overflow-x-auto" data-testid="aga-temporal-tab">
            <h3 className="font-display text-lg font-medium text-brand mb-3">Evolução das escalas ao longo do tempo</h3>
            <table className="w-full text-sm min-w-[600px]">
                <thead>
                    <tr className="text-left text-xs text-muted-clinical border-b border-[var(--border-organic)]">
                        <th className="py-2 pr-3">Escala</th>
                        {timeline.map((t) => (
                            <th key={t.consult.id} className="py-2 px-2 whitespace-nowrap">{fmtDate(t.consult.scheduled_at || t.consult.created_at)}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {AGA_METRICS.map((m) => {
                        const values = timeline.map((t) => m.from(t.assessment?.data));
                        const hasAny = values.some((v) => v !== null);
                        if (!hasAny) return null;
                        return (
                            <tr key={m.key} className="border-b border-[var(--border-organic)] last:border-0">
                                <td className="py-2 pr-3 text-brand-secondary font-medium">{m.label}</td>
                                {values.map((v, i) => {
                                    const prev = i > 0 ? values[i - 1] : null;
                                    const trend = v !== null && prev !== null ? (v > prev ? "up" : v < prev ? "down" : "flat") : null;
                                    return (
                                        <td key={i} className="py-2 px-2 whitespace-nowrap">
                                            {v === null ? <span className="text-muted-clinical">—</span> : (
                                                <span className="flex items-center gap-1">
                                                    <strong className="text-brand">{v}</strong>
                                                    {trend === "up" && <span className="text-[var(--status-danger)] text-xs">↑</span>}
                                                    {trend === "down" && <span className="text-[var(--status-success)] text-xs">↓</span>}
                                                    {trend === "flat" && <span className="text-muted-clinical text-xs">➡</span>}
                                                </span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <p className="text-xs text-muted-clinical mt-3">
                As setas comparam com a consulta anterior. Setas ↑ podem indicar piora (ex.: NPI-Q, quedas) ou melhora (ex.: MMSE, Katz), interprete conforme a escala.
            </p>
        </div>
    );
}

// ---------- Tab: Evoluções unified ----------
function EvolucoesTab({ chart }) {
    const { evolutions, doctors } = chart;
    if (evolutions.length === 0) {
        return <div className="card-earthy text-sm text-muted-clinical">Sem evoluções registradas.</div>;
    }
    return (
        <div className="space-y-3" data-testid="evolucoes-tab-list">
            {evolutions.map((ev) => {
                const doc = doctors[ev.author_id] || doctors[ev.professional_id];
                return (
                    <div key={ev.id} className="card-earthy" data-testid={`evolucao-card-${ev.id}`}>
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <div className="text-xs text-muted-clinical">
                                {fmtDateTime(ev.created_at)} · {typeLabel[ev.consultation_type] || "—"}
                                {doc && ` · ${doc.name}`}
                            </div>
                            <Link to={`/consultations/${ev.consultation_id}`} className="text-xs text-brand hover:underline">Abrir consulta →</Link>
                        </div>
                        <p className="text-sm whitespace-pre-wrap text-brand-secondary">{ev.content || ev.notes || "—"}</p>
                    </div>
                );
            })}
        </div>
    );
}

// ---------- Tab: Planos ----------
function PlanosTab({ chart }) {
    const { care_plans, consultations } = chart;
    const consultById = Object.fromEntries(consultations.map((c) => [c.id, c]));
    if (care_plans.length === 0) {
        return <div className="card-earthy text-sm text-muted-clinical">Nenhum plano de cuidado gerado.</div>;
    }
    return (
        <div className="space-y-3" data-testid="planos-tab-list">
            {care_plans.map((p) => {
                const c = consultById[p.consultation_id];
                return (
                    <div key={p.id} className="card-earthy" data-testid={`plano-card-${p.id}`}>
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <div className="text-xs text-muted-clinical">
                                Plano de {fmtDateTime(p.updated_at || p.created_at)}
                                {c && ` · ${typeLabel[c.consultation_type]}`}
                            </div>
                            {c && <Link to={`/consultations/${c.id}`} className="text-xs text-brand hover:underline">Abrir consulta →</Link>}
                        </div>
                        {p.patient_plan && (
                            <div className="mb-3">
                                <p className="text-xs font-semibold text-brand uppercase tracking-wider mb-1">Plano para o paciente</p>
                                <div className="text-sm text-brand-secondary"><FormattedMarkdown text={p.patient_plan} /></div>
                            </div>
                        )}
                        {p.summary && (
                            <details>
                                <summary className="text-xs font-semibold text-brand uppercase tracking-wider cursor-pointer">Plano clínico (IA)</summary>
                                <div className="text-sm text-brand-secondary mt-2"><FormattedMarkdown text={p.summary} /></div>
                            </details>
                        )}
                        {(p.problems || []).length > 0 && (
                            <div className="mt-3">
                                <p className="text-xs font-semibold text-brand uppercase tracking-wider mb-1">Problemas estruturados</p>
                                <ul className="space-y-1 text-sm">
                                    {p.problems.map((pr, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <PriorityDot priority={pr.priority} />
                                            <span>{pr.title}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ---------- Tab: Medicações ----------
function extractMeds(data) {
    if (!data) return [];
    const buckets = [
        data.medications,
        data?.medicamentos?.medications,
        data?.historico_farmacologico?.medications,
        data?.reconciliacao_medicamentosa?.medicamentos,
        data?.medicacoes_em_uso,
    ];
    for (const b of buckets) {
        if (Array.isArray(b) && b.length > 0) return b;
    }
    return [];
}

function MedicacoesTab({ chart }) {
    const { assessments, consultations } = chart;
    const byConsult = new Map();
    for (const a of assessments) byConsult.set(a.consultation_id, a);
    const timeline = [...consultations]
        .slice()
        .sort((a, b) => (b.scheduled_at || b.created_at || "").localeCompare(a.scheduled_at || a.created_at || ""));

    const rows = timeline
        .map((c) => ({ consult: c, meds: extractMeds(byConsult.get(c.id)?.data) }))
        .filter((r) => r.meds.length > 0);

    if (rows.length === 0) {
        return <div className="card-earthy text-sm text-muted-clinical">Nenhuma reconciliação medicamentosa registrada.</div>;
    }
    return (
        <div className="space-y-3" data-testid="medicacoes-tab-list">
            {rows.map((r, idx) => (
                <div key={r.consult.id} className="card-earthy">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div className="text-xs text-muted-clinical">
                            {idx === 0 && <span className="mr-2 px-2 py-0.5 bg-alt text-brand rounded font-semibold">ATUAL</span>}
                            {fmtDate(r.consult.scheduled_at || r.consult.created_at)} · {typeLabel[r.consult.consultation_type]}
                        </div>
                        <Link to={`/consultations/${r.consult.id}`} className="text-xs text-brand hover:underline">Abrir consulta →</Link>
                    </div>
                    <MedsList meds={r.meds} />
                </div>
            ))}
        </div>
    );
}

// ---------- Tab: Documentos ----------
function DocumentosTab({ chart, patientId }) {
    const { consultations } = chart;
    const [downloading, setDownloading] = useState(null);

    const download = async (path, filename, key) => {
        setDownloading(key);
        try {
            const res = await api.get(path, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = filename; a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setDownloading(null); }
    };

    if (consultations.length === 0) {
        return <div className="card-earthy text-sm text-muted-clinical">Sem documentos disponíveis (nenhuma consulta ainda).</div>;
    }

    return (
        <div className="space-y-3">
            <div className="card-earthy">
                <h3 className="font-display text-lg font-medium text-brand mb-3">Exportar por consulta</h3>
                <div className="space-y-2">
                    {consultations.map((c) => (
                        <div key={c.id} className="flex items-center justify-between flex-wrap gap-2 border-b border-[var(--border-organic)] last:border-0 pb-2 last:pb-0">
                            <div className="text-sm">
                                <p className="font-medium text-brand">{typeLabel[c.consultation_type]}</p>
                                <p className="text-xs text-muted-clinical">{fmtDateTime(c.scheduled_at || c.created_at)}</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    data-testid={`doc-pdf-full-${c.id}`}
                                    onClick={() => download(`/consultations/${c.id}/pdf`, `prontuario_${c.id.slice(0, 8)}.pdf`, `full-${c.id}`)}
                                    disabled={downloading === `full-${c.id}`}
                                    className="text-xs px-3 py-1.5 rounded-lg text-brand border border-brand hover:bg-alt transition-colors flex items-center gap-1.5"
                                >
                                    {downloading === `full-${c.id}` ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Prontuário
                                </button>
                                <button
                                    data-testid={`doc-pdf-summary-${c.id}`}
                                    onClick={() => download(`/consultations/${c.id}/summary-pdf`, `resumo_${c.id.slice(0, 8)}.pdf`, `sum-${c.id}`)}
                                    disabled={downloading === `sum-${c.id}`}
                                    className="text-xs px-3 py-1.5 rounded-lg text-brand-secondary border border-[var(--border-organic)] hover:bg-alt transition-colors flex items-center gap-1.5"
                                >
                                    {downloading === `sum-${c.id}` ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Resumo cuidador
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <CaregiverManager patientId={patientId} />
            <PatientUserLink patientId={patientId} currentEmail={chart.patient.email} currentUserId={chart.patient.user_id} onLinked={() => window.location.reload()} />
        </div>
    );
}

function DeleteConfirm({ patient, confirmText, setConfirmText, deleting, onCancel, onConfirm }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="delete-patient-modal">
            <div className="card-earthy max-w-md w-full space-y-4 bg-white">
                <div className="flex items-center gap-3 text-[var(--status-danger)]">
                    <Trash2 size={24} />
                    <h2 className="font-display text-xl font-medium">Excluir paciente</h2>
                </div>
                <p className="text-sm text-[var(--text-secondary)]">
                    Esta ação é <strong>irreversível</strong>. Serão apagados o cadastro de{" "}
                    <strong className="text-brand">{patient.full_name}</strong> e{" "}
                    todos os dados relacionados: consultas, avaliações AGA, planos de cuidado,
                    evoluções, lembretes de medicamentos, vínculos de cuidador e ciclos de faturamento.
                </p>
                <div>
                    <label className="label-clinical">Para confirmar, digite o nome completo do paciente:</label>
                    <input
                        data-testid="delete-patient-confirm-input"
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={patient.full_name}
                        className="input-clinical"
                        autoFocus
                    />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onCancel} className="btn-ghost" disabled={deleting}>Cancelar</button>
                    <button
                        data-testid="delete-patient-confirm-btn"
                        onClick={onConfirm}
                        disabled={deleting || confirmText.trim() !== patient.full_name.trim()}
                        className="px-4 py-2 rounded-lg bg-[var(--status-danger)] text-white font-medium hover:bg-[#7a1f21] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                        {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        {deleting ? "Excluindo..." : "Excluir definitivamente"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function PatientUserLink({ patientId, currentEmail, currentUserId, onLinked }) {
    const [email, setEmail] = useState(currentEmail || "");
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post(`/patients/${patientId}/link-user`, { email });
            toast.success("Conta do paciente vinculada");
            onLinked && onLinked();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    return (
        <div className="card-earthy">
            <div className="flex items-center gap-2 mb-3">
                <User size={18} className="text-brand" />
                <h3 className="font-display text-lg font-medium text-brand">Conta do paciente (LGPD)</h3>
            </div>
            {currentUserId ? (
                <p className="text-sm text-brand-secondary mb-3 flex items-center gap-2">
                    <LinkIcon size={14} /> Conta vinculada: <strong>{currentEmail}</strong>
                </p>
            ) : (
                <p className="text-sm text-muted-clinical mb-3">
                    Vincule uma conta (role paciente) para permitir que o próprio paciente acesse seu plano de cuidado e prontuário.
                </p>
            )}
            <form onSubmit={submit} className="flex gap-2">
                <input
                    data-testid="patient-link-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="paciente@email.com"
                    className="input-clinical flex-1"
                />
                <button data-testid="patient-link-btn" disabled={loading} className="btn-primary">
                    {currentUserId ? "Atualizar" : "Vincular"}
                </button>
            </form>
        </div>
    );
}
